import { useTranslation } from 'react-i18next';
import { Select, Input } from '@/components/ui';
import { StepProps } from '../types';

/**
 * StyleStep Component
 *
 * Step 3: Tone & Style configuration
 * - Tone selection (12 predefined options)
 * - Formality slider (0-10)
 * - Post length (short/medium/long)
 * - Language (free text input)
 */

const TONE_OPTIONS = [
  { value: 'friendly_professional', label: 'ai_briefing_tone_friendly_professional' },
  { value: 'authoritative_informative', label: 'ai_briefing_tone_authoritative_informative' },
  { value: 'urgent_persuasive', label: 'ai_briefing_tone_urgent_persuasive' },
  { value: 'casual_conversational', label: 'ai_briefing_tone_casual_conversational' },
  { value: 'professional_trustworthy', label: 'ai_briefing_tone_professional_trustworthy' },
  { value: 'humorous_informal', label: 'ai_briefing_tone_humorous_informal' },
  { value: 'professional_straightforward', label: 'ai_briefing_tone_professional_straightforward' },
  { value: 'serious_empathetic', label: 'ai_briefing_tone_serious_empathetic' },
  { value: 'positive_enthusiastic', label: 'ai_briefing_tone_positive_enthusiastic' },
  { value: 'authoritative_professional', label: 'ai_briefing_tone_authoritative_professional' },
  { value: 'casual_funny', label: 'ai_briefing_tone_casual_funny' },
  { value: 'authoritative_expert', label: 'ai_briefing_tone_authoritative_expert' },
];

const LENGTH_OPTIONS = [
  { value: 'short', label: 'ai_briefing_length_short' },
  { value: 'medium', label: 'ai_briefing_length_medium' },
  { value: 'long', label: 'ai_briefing_length_long' },
];

export function StyleStep({ formData, onFieldChange, errors }: StepProps) {
  const { t } = useTranslation('ai');

  // Translate tone and length options
  const translatedToneOptions = TONE_OPTIONS.map(opt => ({
    value: opt.value,
    label: t(opt.label),
  }));

  const translatedLengthOptions = LENGTH_OPTIONS.map(opt => ({
    value: opt.value,
    label: t(opt.label),
  }));

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {/* Tone */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          {t('ai_briefing_tone_label')}
        </label>
        <Select
          options={translatedToneOptions}
          value={formData.tone}
          onValueChange={(value) => onFieldChange('tone', value)}
          placeholder={t('ai_briefing_tone_placeholder')}
          error={errors?.tone}
        />
      </div>

      {/* Length */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          {t('ai_briefing_length_label')}
        </label>
        <Select
          options={translatedLengthOptions}
          value={formData.length}
          onValueChange={(value) => onFieldChange('length', value as 'short' | 'medium' | 'long')}
          placeholder={t('ai_briefing_length_placeholder')}
          error={errors?.length}
        />
      </div>

      {/* Formality Slider */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          {t('ai_briefing_formality_label')}
        </label>
        <div className="flex items-center gap-2 h-[46px]">
          <span className="text-[10px] text-gray-400 whitespace-nowrap">
            {t('ai_briefing_formality_casual')}
          </span>
          <input
            type="range"
            min="0"
            max="10"
            value={formData.formality}
            onChange={(e) => onFieldChange('formality', parseInt(e.target.value, 10))}
            className="flex-1 h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer range-teal"
          />
          <span className="text-[10px] text-gray-400 whitespace-nowrap">
            {t('ai_briefing_formality_professional')}
          </span>
          <span className="text-xs font-semibold text-primary-600 bg-primary-50 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0">
            {formData.formality}
          </span>
        </div>
        {errors?.formality && (
          <p className="mt-1 text-xs text-danger" role="alert">
            {errors.formality}
          </p>
        )}
      </div>

      {/* Language */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          {t('ai_briefing_language_label')}
        </label>
        <Input
          type="text"
          value={formData.language}
          onChange={(e) => onFieldChange('language', e.target.value)}
          placeholder={t('ai_briefing_language_placeholder')}
          error={errors?.language}
        />
        <p className="mt-1 text-xs text-gray-400">
          {t('ai_briefing_language_help')}
        </p>
      </div>
    </div>
  );
}
